import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, FileText, Plus, Loader2, Trash2, Wrench, Pencil, Lock } from "lucide-react"; // Added Pencil
import { Button } from "@/components/ui/button";
import GlassCard from "@/components/GlassCard";
import SwipeableCard from "@/components/SwipeableCard";
import SmartCaptureSheet from "@/components/SmartCaptureSheet";
import AddApplianceSheet from "@/components/AddApplianceSheet"; // Added
import AddFurnitureSheet from "@/components/AddFurnitureSheet"; // Added
import UpgradePlanSheet from "@/components/UpgradePlanSheet"; // Added
import WarrantyModal from "@/components/WarrantyModal"; // Fix: warranty doc had no viewer entry point
import { useServiceLogs } from "@/hooks/useServiceLogs";
import { useAppliances } from "@/hooks/useAppliances";
import { useFurniture } from "@/hooks/useFurniture";
import { useSubscription } from "@/hooks/useSubscription";
import { useRoutineTasks } from "@/hooks/useRoutineTasks";
import { calculateNextServiceDate } from "@/lib/dateUtils";
import { toast } from "@/hooks/use-toast";

import FilePreviewModal from "@/components/FilePreviewModal";
import { cn } from "@/lib/utils";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ProductDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isLogServiceOpen, setIsLogServiceOpen] = useState(false);
    const [isEditSheetOpen, setIsEditSheetOpen] = useState(false); // Added
    const [isDeleteApplianceOpen, setIsDeleteApplianceOpen] = useState(false);
    const [logToDelete, setLogToDelete] = useState(null);
    const [previewItem, setPreviewItem] = useState(null);
    const [showUpgradeSheet, setShowUpgradeSheet] = useState(false);
    // Fix: warranty document upload — ProductDetails already had a "View
    // Purchase Receipt" button, but nothing surfaced the uploaded warranty
    // document (warranty_url), so there was no way to view it after upload.
    const [showWarrantyModal, setShowWarrantyModal] = useState(false);
    const { isFeatureLocked, plan } = useSubscription();
    const { applianceId, itemType, item: stateItem } = location.state || {};
    const isFurniture = itemType === "furniture";

    const { appliances, deleteAppliance, updateAppliance, refetch: refetchAppliances } = useAppliances(); // Added updateAppliance
    const { furniture, deleteFurniture, updateFurniture, refetch: refetchFurniture } = useFurniture();
    const { addRoutineTask } = useRoutineTasks();
    const appliance = isFurniture ? furniture?.find(f => f.id === applianceId) : appliances?.find(a => a.id === applianceId);
    const [isDeletingAppliance, setIsDeletingAppliance] = useState(false);

    // Fetch service logs for this specific appliance
    const { serviceLogs, loading: logsLoading, refetch, deleteServiceLog } = useServiceLogs(undefined, applianceId);

    // Limit display for Free Tier users
    const isHistoryLocked = isFeatureLocked('maintenance_history', 2);
    const displayLogs = isHistoryLocked ? serviceLogs.slice(0, 1) : serviceLogs;
    const hiddenLogsCount = serviceLogs.length - displayLogs.length;

    // Fallback logic: prefer live appliance data, then state item, then defaults
    const product = appliance ? {
        name: `${appliance.brand || ""} ${appliance.name}`.trim(),
        details: appliance.installation_date ? `Installed ${new Date(appliance.installation_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}` : appliance.category || "Appliance",
        merchant: appliance.brand || "Unknown",
        purchaseDate: appliance.installation_date,
        warrantyExpiry: appliance.warranty_expiry,
        lastServiceDate: appliance.last_service_date,
        nextServiceDate: appliance.next_service_date,
    } : stateItem || {
        name: "Loading...",
        details: "",
        merchant: "Unknown",
        purchaseDate: "",
        warrantyExpiry: "",
    };

    // Get suggestions    // Empty

    const handleDeleteLog = async () => {
        if (!logToDelete) return;
        const success = await deleteServiceLog(logToDelete.id);
        if (success) {
            setLogToDelete(null);
            isFurniture ? refetchFurniture() : refetchAppliances();
        }
    };

    const handleDeleteAppliance = async () => {
        setIsDeletingAppliance(true);
        try {
            if (isFurniture) {
                await deleteFurniture(applianceId);
            } else {
                await deleteAppliance(applianceId);
            }
            setIsDeleteApplianceOpen(false);
            navigate("/home-records", { state: { activePopup: isFurniture ? "furniture" : "appliances" } });
        } catch (error) {
            console.error("Failed to delete item:", error);
            toast({
                title: "Error",
                description: "Failed to delete item. Please try again.",
                variant: "destructive"
            });
        } finally {
            setIsDeletingAppliance(false);
        }
    };

    const handleLogSuccess = () => {
        refetch(); // Refetch logs
        isFurniture ? refetchFurniture() : refetchAppliances(); // Refetch appliance (to update next service date)
    };

    const handleEditSave = async (updatedData) => {
        if (!applianceId) return false;
        try {
            if (isFurniture) {
                await updateFurniture(applianceId, updatedData);
                refetchFurniture();
            } else {
                await updateAppliance(applianceId, updatedData);
                refetchAppliances();
            }
            return true;
        } catch (error) {
            console.error("Failed to update:", error);
            return false;
        }
    };

    const handleMarkSubTaskDone = async (subTaskId, subTaskName) => {
        if (!appliance) return;

        const updatedTasks = (appliance.maintenance_tasks || []).map(task => {
            if (task.id === subTaskId) {
                const todayStr = new Date().toISOString().split("T")[0];
                const nextDate = calculateNextServiceDate(todayStr, task.frequency, task.frequency_unit);
                return {
                    ...task,
                    last_service_date: todayStr,
                    next_service_date: nextDate
                };
            }
            return task;
        });

        if (isFurniture) {
            await updateFurniture(appliance.id, { maintenance_tasks: updatedTasks });
        } else {
            await updateAppliance(appliance.id, { maintenanceTasks: updatedTasks });
        }

        await addRoutineTask({
            property_id: appliance.property_id,
            item_type: isFurniture ? 'furniture' : 'appliance',
            item_id: appliance.id,
            task_name: subTaskName,
            completed_at: new Date().toISOString()
        });

        toast({
            title: "Sub-task Completed",
            description: `"${subTaskName}" has been marked as complete.`,
        });

        isFurniture ? refetchFurniture() : refetchAppliances();
    };

    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] pb-24">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-40 bg-primary/95 backdrop-blur-sm" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
                <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between"> {/* Changed to justify-between */}
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(-1)}
                            className="text-primary-foreground hover:bg-primary-foreground/10"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <h1 className="text-base font-medium text-primary-foreground">
                            Product Details
                        </h1>
                    </div>
                    {appliance && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setIsEditSheetOpen(true)}
                            className="text-primary-foreground hover:bg-primary-foreground/10"
                        >
                            <Pencil className="w-5 h-5" />
                        </Button>
                    )}
                </div>
            </header>

            {/* Main Content */}
            <main className="px-6" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 5rem)" }}>
                <div className="max-w-lg mx-auto space-y-6">
                    {/* ... (rest of main content) ... */}
                    {/* Product Header */}
                    <div className="text-center pt-4">
                        <h2 className="text-2xl font-bold text-foreground">{product.name}</h2>
                        <p className="text-muted-foreground mt-1">{product.details}</p>
                    </div>

                    {/* Product Info Card */}
                    <GlassCard className="p-5 space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-xl bg-primary/10">
                                <MapPin className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Brand</p>
                                <p className="font-medium text-foreground">{product.merchant}</p>
                            </div>
                        </div>

                        {product.purchaseDate && (
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-primary/10">
                                    <Calendar className="w-4 h-4 text-primary" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Installation Date</p>
                                    <p className="font-medium text-foreground">
                                        {new Date(product.purchaseDate).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </p>
                                </div>
                            </div>
                        )}

                        {product.warrantyExpiry && (
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-primary/10">
                                    <FileText className="w-4 h-4 text-primary" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Warranty Until</p>
                                    <p className="font-medium text-foreground">
                                        {new Date(product.warrantyExpiry).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </p>
                                </div>
                            </div>
                        )}

                        {product.lastServiceDate && (
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-primary/10">
                                    <Wrench className="w-4 h-4 text-primary" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Last Serviced</p>
                                    <p className="font-medium text-foreground">
                                        {new Date(product.lastServiceDate).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        {product.lastServiceDate === product.purchaseDate && " (New)"}
                                    </p>
                                </div>
                            </div>
                        )}

                        {product.nextServiceDate && (
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-primary/10">
                                    <Calendar className="w-4 h-4 text-primary" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Next Service Scheduled</p>
                                    <p className={`font-medium ${new Date(product.nextServiceDate) < new Date() ? "text-destructive" : "text-primary"}`}>
                                        {new Date(product.nextServiceDate).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        {new Date(product.nextServiceDate) < new Date() && " (Overdue)"}
                                    </p>
                                </div>
                            </div>
                        )}

                        {appliance?.receipt_url && (
                            <div className="pt-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full h-11 gap-2 rounded-2xl text-primary border-primary/20 bg-primary/5 hover:bg-primary/10 font-bold"
                                    onClick={() => setPreviewItem({ url: appliance.receipt_url, name: "Purchase Receipt" })}
                                >
                                    <FileText className="w-4 h-4" />
                                    View Purchase Receipt
                                </Button>
                            </div>
                        )}

                        {appliance?.warranty_url && (
                            <div className={appliance?.receipt_url ? "" : "pt-2"}>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full h-11 gap-2 rounded-2xl text-primary border-primary/20 bg-primary/5 hover:bg-primary/10 font-bold"
                                    onClick={() => setShowWarrantyModal(true)}
                                >
                                    <FileText className="w-4 h-4" />
                                    View Warranty
                                </Button>
                            </div>
                        )}
                    </GlassCard>

                    {/* Log Service Button */}
                    <Button
                        onClick={() => {
                            if (isFeatureLocked('maintenance_history', serviceLogs.length)) {
                                setShowUpgradeSheet(true);
                            } else {
                                setIsLogServiceOpen(true);
                            }
                        }}
                        className={cn(
                            "w-full gap-2",
                            isFeatureLocked('maintenance_history', serviceLogs.length) && "border-amber-500/50 text-amber-700 bg-amber-50 hover:bg-amber-100"
                        )}
                        variant={isFeatureLocked('maintenance_history', serviceLogs.length) ? "outline" : "default"}
                        size="lg"
                    >
                        {isFeatureLocked('maintenance_history', serviceLogs.length) ? <Lock className="w-4 h-4 text-amber-500" /> : <Plus className="w-4 h-4" />}
                        {isFeatureLocked('maintenance_history', serviceLogs.length) ? "Upgrade to Log More" : "Log Service"}
                    </Button>

                    {/* Routine Tasks list */}
                    {appliance?.maintenance_tasks && appliance.maintenance_tasks.length > 0 && (
                        <section className="space-y-4">
                            <h3 className="text-lg font-semibold text-foreground">Routine Tasks</h3>
                            <div className="space-y-3">
                                {appliance.maintenance_tasks.map((task) => {
                                    const nextDate = new Date(task.next_service_date);
                                    const today = new Date();
                                    nextDate.setHours(0, 0, 0, 0);
                                    today.setHours(0, 0, 0, 0);
                                    const isOverdue = nextDate < today;
                                    const formattedLastDone = task.last_service_date
                                        ? new Date(task.last_service_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
                                        : "Never";

                                    return (
                                        <GlassCard key={task.id} className="p-4 space-y-3">
                                            <div className="flex justify-between items-start">
                                                <div className="space-y-1">
                                                    <h4 className="font-bold text-foreground">{task.name}</h4>
                                                    <p className="text-xs text-muted-foreground">
                                                        Every {task.frequency} {task.frequency_unit} • Last done: {formattedLastDone}
                                                    </p>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleMarkSubTaskDone(task.id, task.name)}
                                                    className="h-8 rounded-full px-3 text-xs bg-primary/5 hover:bg-primary text-primary hover:text-white font-semibold border-primary/20 hover:border-transparent transition-all"
                                                >
                                                    Mark Done
                                                </Button>
                                            </div>
                                            <div className="flex items-center gap-2 pt-1 border-t border-primary/5">
                                                <span className="text-[10px] font-medium text-muted-foreground">
                                                    Next service:
                                                </span>
                                                <span className={cn(
                                                    "text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider",
                                                    isOverdue ? "text-destructive bg-destructive/10" : "text-primary bg-primary/10"
                                                )}>
                                                    {new Date(task.next_service_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                    {isOverdue && " (Overdue)"}
                                                </span>
                                            </div>
                                        </GlassCard>
                                    );
                                })}
                            </div>
                        </section>
                    )}


                    {/* Service History */}
                    <section className="relative">
                        <div className="flex items-center gap-2 mb-4">
                            <h3 className="text-lg font-semibold text-foreground">Service History</h3>
                            {(plan === 'free' && serviceLogs.length >= 1) && <Lock className="w-3 h-3 text-amber-500" />}
                        </div>
                        <div className="space-y-3">
                            {logsLoading ? (
                                <div className="flex justify-center py-8">
                                    <Loader2 className="w-6 h-6 text-primary animate-spin" />
                                </div>
                            ) : serviceLogs.length === 0 ? (
                                <GlassCard className="p-6">
                                    <div className="text-center">
                                        <Plus className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                                        <p className="text-sm text-muted-foreground">
                                            No service history yet for this appliance.
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Tap "Log Service" to add your first entry.
                                        </p>
                                    </div>
                                </GlassCard>
                            ) : (
                                <>
                                    {displayLogs.map((log) => (
                                        <SwipeableCard
                                            key={log.id}
                                            onDelete={() => setLogToDelete(log)}
                                            onClick={() => {
                                                navigate("/maintenance-detail", {
                                                    state: {
                                                        item: {
                                                            ...log,
                                                            name: product.name,
                                                            details: log.description || "Service performed",
                                                            invoiceUrl: log.invoice_url,
                                                            purchaseDate: log.date,
                                                            merchant: appliance?.brand || product.merchant
                                                        }
                                                    }
                                                });
                                            }}
                                        >
                                            <GlassCard className="p-4 active:scale-[0.98] transition-all">
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium text-foreground">
                                                            {log.description || "Service performed"}
                                                        </p>
                                                        {log.cost && (
                                                            <p className="text-sm text-primary font-medium mt-1">
                                                                R{log.cost.toLocaleString()}
                                                            </p>
                                                        )}
                                                        {log.invoice_url && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setPreviewItem({ url: log.invoice_url, name: log.description || 'Receipt' });
                                                                }}
                                                                className="text-xs text-primary underline mt-1 inline-block"
                                                            >
                                                                View Invoice/Receipt
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-muted-foreground">
                                                            {new Date(log.date).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' })}
                                                        </span>
                                                    </div>
                                                </div>
                                            </GlassCard>
                                        </SwipeableCard>
                                    ))}

                                    {hiddenLogsCount > 0 && (
                                        <div
                                            onClick={() => setShowUpgradeSheet(true)}
                                            className="w-full bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4 flex flex-col items-center justify-center text-center cursor-pointer active:scale-95 transition-all mt-4"
                                        >
                                            <Lock className="w-6 h-6 text-amber-500 mb-2" />
                                            <p className="text-sm font-bold text-amber-900">
                                                {hiddenLogsCount} past record{hiddenLogsCount !== 1 ? 's' : ''} hidden
                                            </p>
                                            <p className="text-xs text-amber-700/80 mt-1">
                                                Tap to upgrade and unlock full service history
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </section>

                    {/* Delete Item Button */}
                    <div className="pt-8 pb-12">
                        <Button
                            variant="destructive"
                            className="w-full gap-2 rounded-2xl h-14"
                            onClick={() => setIsDeleteApplianceOpen(true)}
                            disabled={isDeletingAppliance}
                        >
                            <Trash2 className="w-5 h-5" />
                            {isDeletingAppliance ? "Deleting..." : isFurniture ? "Delete Furniture" : "Delete Appliance"}
                        </Button>
                        <p className="text-center text-xs text-muted-foreground mt-3">
                            This will permanently remove this {isFurniture ? "furniture item" : "appliance"} and all its service history.
                        </p>
                    </div>
                </div>
            </main>

            {/* Smart Capture Sheet */}
            <SmartCaptureSheet
                open={isLogServiceOpen}
                onOpenChange={setIsLogServiceOpen}
                applianceId={applianceId}
                onSuccess={handleLogSuccess}
            />

            {/* Edit Appliance or Furniture Sheet */}
            {isFurniture ? (
                <AddFurnitureSheet
                    open={isEditSheetOpen}
                    onOpenChange={setIsEditSheetOpen}
                    initialData={appliance}
                    onSave={handleEditSave}
                />
            ) : (
                <AddApplianceSheet
                    open={isEditSheetOpen}
                    onOpenChange={setIsEditSheetOpen}
                    initialData={appliance}
                    onSave={handleEditSave}
                />
            )}            {/* Delete Confirmation Dialog */}
            <AlertDialog open={isDeleteApplianceOpen} onOpenChange={setIsDeleteApplianceOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete {isFurniture ? "Furniture" : "Appliance"}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{product.name}"? This will remove all service records and cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteAppliance}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Service Log Confirmation Dialog */}
            <AlertDialog open={!!logToDelete} onOpenChange={(open) => !open && setLogToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Service Log?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{logToDelete?.description || "this service log"}"? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setLogToDelete(null)}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteLog}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <FilePreviewModal
                open={!!previewItem}
                onOpenChange={(open) => !open && setPreviewItem(null)}
                fileUrl={previewItem?.url}
                fileName={previewItem?.name}
            />

            <WarrantyModal
                open={showWarrantyModal}
                onOpenChange={setShowWarrantyModal}
                item={appliance ? {
                    name: product.name,
                    warrantyDocUrl: appliance.warranty_url,
                    merchant: appliance.brand,
                    purchaseDate: appliance.installation_date,
                    warrantyExpiry: appliance.warranty_expiry,
                } : null}
            />

            <UpgradePlanSheet
                open={showUpgradeSheet}
                onOpenChange={setShowUpgradeSheet}
                currentPlan={plan}
            />
        </div>
    );
};

export default ProductDetails;
